import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { DUMMY_PAYLOADS } from './constant';
import { environment } from '../environments/environment';

export interface TenantItem {
  tenant_guid: string;
  tenant_name: string;
  tenant_status: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoanAiService {
  // myUrl = 'https://10.176.129.101:8000/'
  myUrl = environment.apiUrl;
  rrUrl = environment.apiUrl;
  private pdfBlobSubject = new Subject<Blob>();
  private pdfLoadingSubject = new Subject<boolean>();
  private pdfClearSubject = new Subject<void>();
  private pdfPageNumberSubject = new BehaviorSubject<number>(1);
  private tenantIdSubject = new BehaviorSubject<string>('');
  private tenantsSubject = new BehaviorSubject<TenantItem[]>([]);
  tenantId$ = this.tenantIdSubject.asObservable();

  constructor(private httpClient: HttpClient) {
    // Restore tenant/engagement id on hard refresh from localStorage or environment
    try {
      const stored = (typeof localStorage !== 'undefined')
        ? (localStorage.getItem('tenantId') || '').trim()
        : '';
      if (stored) {
        this.tenantIdSubject.next(stored);
      } else {
        const fallback = ((environment as any).tenant_id || (environment as any).engagement_id || '').toString().trim();
        if (fallback) {
          this.tenantIdSubject.next(fallback);
        }
      }
    } catch (e) {
      // ignore storage access errors
    }
  }

  setTenantId(tenantid: string) {
    this.tenantIdSubject.next(tenantid);
    localStorage.setItem('tenantId', tenantid);
  }

  fetchAllProducts() {
    return this.httpClient.get<any>(
      this.rrUrl + 'loan-review/v1/get-all-products'
    );
  }

  fetchAllAssets() {
    return this.httpClient.get<any>(
      this.rrUrl + 'loan-review/v1/get-all-asset-classes'
    );
  }

  fetchAllTenants() {
    return this.httpClient.get<TenantItem[]>(this.rrUrl + 'client-list');
  }

  loadTenants() {
    this.fetchAllTenants().subscribe(
      (tenants: TenantItem[]) => {
        // Store the fetched tenants in the BehaviorSubject
        this.tenantsSubject.next(tenants);
        // console.log('Fetched Tenants:', tenants);
      },
      (err) => {
        console.error('Error fetching tenants:', err);
      }
    );
  }

  get tenants$() {
    return this.tenantsSubject.asObservable(); // Expose the BehaviorSubject as an observable
  }

  pdfBlob$ = this.pdfBlobSubject.asObservable();
  pdfLoading$ = this.pdfLoadingSubject.asObservable();
  pdfClear$ = this.pdfClearSubject.asObservable();
  pdfPageNumber$ = this.pdfPageNumberSubject.asObservable();

  clearPdfViewer() {
    this.pdfClearSubject.next();
  }

  sendPdfBlob(blob: Blob, pageNumber?: number) {
    this.pdfLoadingSubject.next(true);
    this.pdfBlobSubject.next(blob);
    if (typeof pageNumber === 'number') {
      this.pdfPageNumberSubject.next(pageNumber);
    }
    setTimeout(() => this.pdfLoadingSubject.next(false), 100);
  }

  private getEngagementId(): string {
    if ((environment as any).engagement_id) {
      return (environment as any).engagement_id;
    }
    return environment.production ? 'nexbank' : 'applebank';
  }

  FetchSharepoint() {
    let params = {
      tenant_id: this.tenantIdSubject.getValue(),
      engagement_id: this.tenantIdSubject.getValue(),
      sharepoint_url: '',
      sharepoint_root_folder_path: '',
      use_case_name: DUMMY_PAYLOADS[0].engagement_name,
      is_open_source_pipeline: DUMMY_PAYLOADS[0].is_open_source_pipeline,
    };

    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/sharepoint/data-intake',
      params
    );
  }

  pollApi(data: any) {
    let params = {
      request_id: data,
    };
    return this.httpClient.get<any>(
      this.myUrl + 'loan-review/process-data/status',
      { params: params }
    );
  }

  refreshApi() {
    let params = {
      tenant_id: this.tenantIdSubject.getValue(),
      engagement_id: this.tenantIdSubject.getValue(),
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/extract-review-metadata',
      params
    );
  }
  runApi() {
    let params = {
      engagement_id: this.tenantIdSubject.getValue(),
      tenant_id: this.tenantIdSubject.getValue(),
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/process-data/summary ',
      params
    );
  }

  startOrchestration(year: any, quarter: any) {
    let params = {
      tenant_id: this.tenantIdSubject.getValue(),
      engagement_id: this.tenantIdSubject.getValue(),
      use_case_name: DUMMY_PAYLOADS[0].engagement_name,
      is_open_source_pipeline: DUMMY_PAYLOADS[0].is_open_source_pipeline,
      year: year,
      quarter: quarter,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/process-data/start',
      params
    );
  }
  viewDetails(year: any, quarter: any) {
    let params = {
      engagement_id: this.tenantIdSubject.getValue(),
      tenant_id: this.tenantIdSubject.getValue(),
      year: parseInt(year),
      quarter: quarter,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/view-data',
      params
    );
  }
  updateAsset(year: any, quarter: any, val: any, docId: any) {
    let params = {
      engagement_id: this.tenantIdSubject.getValue(),
      year: parseInt(year),
      quarter: quarter,
      doc_run_id: docId,
      asset_class: val,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/update_asset-class',
      params
    );
  }

  setDocumentActiveStatus(doc_id: string, is_active: boolean) {
    const body = {
      doc_id,
      is_active,
    };
    return this.httpClient.put<any>(
      this.myUrl + 'loan-review/docs/set-document-active-status',
      body
    );
  }

  runExtraction(year: any, quarter: any) {
    let params = {
      tenant_id: this.tenantIdSubject.getValue(),
      engagement_id: this.tenantIdSubject.getValue(),
      use_case_name: 'Loan File Review',
      year: parseInt(year),
      quarter: quarter,
      product: 'Loan',
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/extraction/run-job',
      params
    );
  }

  downloadCsv(year: any, quarter: any) {
    const params: any = {
      tenant_id: this.tenantIdSubject.getValue(),
      engagement_id: this.tenantIdSubject.getValue(),
      year: year,
      quarter: quarter,
    };
    // Expect JSON response with base64 content
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/docs/missing-doc-categories-obligors',
      params
    );
  }

  runSingleExtraction(year: any, quarter: any, docRunId: any) {
    let params = {
      tenant_id: this.tenantIdSubject.getValue(),
      engagement_id: this.tenantIdSubject.getValue(),
      use_case_name: 'Loan File Review',
      is_open_source_pipeline: DUMMY_PAYLOADS[0].is_open_source_pipeline,
      year: parseInt(year),
      quarter: quarter,
      product: 'Loan',
      doc_run_id: docRunId,
      is_latest: true,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/extraction/run',
      params
    );
  }

  runSingleJobExtraction(year: any, quarter: any, docRunId: any, obligor_name: string, sub_product: string) {
    const payload = {
      tenant_id: this.tenantIdSubject.getValue(),
      engagement_id: this.tenantIdSubject.getValue(),
      use_case_name: 'Loan File Review',
      year: parseInt(year),
      quarter: quarter,
      product: 'Loan',
      doc_run_id: docRunId,
      obligor_name: obligor_name,
      sub_product: sub_product,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/extraction/run-job',
      payload
    );
  }

  pollExtractionStatus(id: any, docIds: any) {
    let params = {
      request_id: id,
      doc_run_ids: docIds,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/extraction/status',
      params
    );
  }

  pollAttributeStatus(id: any, docIds: any) {
    let params = {
      request_id: id,
      doc_run_ids: docIds,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/poll-attribute-extraction-status',
      params
    );
  }

  stopExtraction(request_id: any, doc_run_id: any) {
    const params: any = {
      request_id: request_id,
      doc_run_id: doc_run_id,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/extraction/cancel-extraction',
      params
    );
  }

  stopDatabricksJobExtraction(request_id: any, doc_run_id: any) {
    const params: any = {
      doc_run_id: doc_run_id,
      request_id: request_id
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/extraction/stop-databricks-extraction',
      params
    );
  }

  getDocRunInfo(doc_run_id: any, year: any, quarter: any) {
    let params: any = {
      tenant_id: this.tenantIdSubject.getValue(),
      engagement_id: this.tenantIdSubject.getValue(),
      doc_run_id: doc_run_id,
    };
    if (year) {
      params.year = year;
    }
    if (quarter) {
      params.quarter = quarter;
    }
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/docs/view-detailed-list',
      params
    );
  }

  startChat(quarter: any, year: any, docId: any, sessionId: string = '') {
    let params = {
      session_id: sessionId,
      doc_quarter: quarter,
      doc_year: year,
      obligor_name: 'Testing Obligor',
      doc_run_id: docId,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/chatbot/start-session',
      params
    );
  }

  continueChat(chatQues: any, sessionId: string = '') {
    let params = {
      session_id: sessionId,
      user_input: chatQues,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/chatbot/chat',
      params
    );
  }

  persistMemory(sessionId: string = ''): Observable<any> {
    let params = {
      session_id: sessionId,
    };
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/chatbot/close-session',
      params
    );
  }
  _dispalyDoc(doc_id: string): Observable<any> {
    let body = {
      doc_id: doc_id,
    };
    return this.httpClient.post(this.myUrl + 'loan-review/docs/display', body, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  getDocLabel() {
    const params = {
      usecase: 'Loan File Review',
    };
    return this.httpClient.get<any>(
      this.myUrl + 'loan-review/v1/loan-file-review/doc-labels',
      { params }
    );
  }

  _updateDocLabel(requestParam: {
    new_label: string;
    doc_id: string;
    old_label: string;
  }): Observable<any> {
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/docs/update-label',
      {
        doc_id: requestParam.doc_id,
        new_label: requestParam.new_label,
        previous_label: requestParam.old_label,
      }
    );
  }

  uploadDocument(
    file: File,
    tenant_id: string,
    engagement_id: string,
    doc_run_id: string,
    year: string,
    quarter: string,
    obligor_name: string,
    facility_type: string
  ): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tenant_id', tenant_id);
    formData.append('engagement_id', engagement_id);
    formData.append('doc_run_id', doc_run_id);
    formData.append('year', year);
    formData.append('quarter', quarter);
    formData.append('obligor_name', obligor_name);
    formData.append('facility_type', facility_type);
    return this.httpClient.post<any>(
      this.myUrl + 'loan-review/repayment-analysis/upload',
      formData
    );
  }
}
